import { mapGetters } from 'vuex';

import { sortLongString } from '@/utils/dashboard';
import { isValueExist } from '@/utils/utils';

import { checkValidFieldByFeatureType } from '@/components/Dashboard/common/WidgetSettingPanel/utils';
import { FEATURE_TYPES } from '@/components/Dashboard/common/WidgetSettingPanel/const';
import {
    BREAKDOWN,
    getUniqueSelectedIds,
    makeAvailableBreakdownFormat,
    setMetricUniqueKey,
    TABLE_METRIC,
} from '@/components/Dashboard/common/WidgetSettingPanel';
import { distinctUntilChanged } from 'rxjs/operators';
import { debounce } from 'lodash';
import { makeCategoryLocaleMap } from '@/components/Dashboard/common/UaFilterSettingPanel';

import { getFieldLocale } from '@/utils/field';

const checkValidDatastreamField = checkValidFieldByFeatureType(
    FEATURE_TYPES.DATASTREAM
);

export default {
    name: 'TableMixin',
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        sourceList: {
            type: Array,
            default: R.always([]),
        },
        metricList: {
            type: Array,
            default: R.always([]),
        },
        page: String,
        adsTableSources: {
            type: Array,
            default: R.always([]),
        },
        breakdownList: {
            type: Array,
            default: R.always([]),
        },
        tableAdAccounts: {
            type: Array,
            default: R.always([]),
        },
        isAnalytic: Boolean,
        streamType: String,
        selectedArchLevels: {
            type: Array,
            default: R.always([]),
        },
    },
    watch: {
        _tableAdAccounts: {
            handler(accounts) {
                this.$emit(
                    'setSourceList',
                    accounts.map((account) => {
                        return {
                            ...account,
                            selected: this.sourceList
                                ?.filter(R.prop('selected'))
                                .some(
                                    (source) =>
                                        source.channelId ===
                                            account.channelId &&
                                        source.channel === account.channel
                                ),
                        };
                    })
                );
            },
            immediate: true,
        },
        sourceList: {
            handler(sources) {
                if (!sources || sources.length === 0) return;

                const { updateAvailableFields } = this;

                const selected = R.filter(R.prop('selected'), sources);

                if (isValueExist(selected)) {
                    updateAvailableFields(selected);
                } else {
                    this.resetTableValues();
                }
            },
            deep: true,
            immediate: true,
        },
    },
    data() {
        return {
            availableMetricList: [],
            availableBreakdownList: [],
            categoryLocaleMap: {},
        };
    },
    computed: {
        ...mapGetters('user', ['isQa', 'userAccountId']),
        selectedSources() {
            return R.filter(R.prop('selected'), this.sourceList);
        },
        currentBreakdownIds() {
            return getUniqueSelectedIds(this.breakdownList);
        },
        _tableAdAccounts() {
            return R.map(R.dissoc('settings'), this.tableAdAccounts);
        },
    },
    methods: {
        updateAvailableFields: debounce(async function (sources = []) {
            const { getAvailableFieldsV2, isAnalytic, currentBreakdownIds } =
                this;

            const params = {
                adAccounts: sources,
                isCustomTable: isAnalytic,
                currentBreakdowns: currentBreakdownIds,
                isForDatastream: true,
            };

            try {
                this.$emit('loading', true);

                const {
                    fields = [],
                    categories,
                    breakdowns,
                } = await getAvailableFieldsV2(params);

                this.categoryLocaleMap = makeCategoryLocaleMap(categories);

                const formattedMetrics = R.map(setMetricUniqueKey)(fields);
                const formattedBreakdowns = R.compose(
                    R.map(makeAvailableBreakdownFormat)
                )(breakdowns);

                this.availableMetricList = formattedMetrics;
                this.availableBreakdownList = formattedBreakdowns;
            } catch (e) {
                console.error(e);
            } finally {
                this.$emit('loading', false);
            }
        }, 200),
        getCurrentListByType(type) {
            switch (type) {
                case TABLE_METRIC:
                    return this.metricList;
                case BREAKDOWN:
                    return this.breakdownList;
            }
        },
        selectedListByType(type) {
            switch (type) {
                case TABLE_METRIC:
                    return this.metricList.filter(R.prop('selected'));
                case BREAKDOWN:
                    return this.breakdownList.filter(R.prop('selected'));
            }
        },
        updateListByType({ type, list }) {
            switch (type) {
                case TABLE_METRIC:
                    return this.$emit('setMetricList', list);
                case BREAKDOWN:
                    return this.$emit('setBreakdownList', list);
            }
        },
        onUpdateMetricWithCheck(v = []) {
            if (v.filter(R.prop('selected')).length >= 100) {
                this.redesignedToast({
                    isError: true,
                    message: this.$t('EXPORT_DATA.warning_TOO_MANY_METRICS'),
                });
                return;
            }

            this.$emit('setMetricList', v);
        },
        refineMetric({ name }) {
            const refinedMetric = getFieldLocale.call(this, name);
            return this.isQa ? `[${name}] - ${refinedMetric}` : refinedMetric;
        },
        resetTableValues() {
            this.$emit('setMetricList', []);
            this.$emit('setBreakdownList', []);
            this.availableMetricList = [];
            this.availableBreakdownList = [];
        },
        observeBreakdown$() {
            const { generateWatchNew$: gen$ } = this;

            const currentBreakdown$ = gen$('currentBreakdownIds').pipe(
                // filter(isValueExist),
                distinctUntilChanged((a, b) =>
                    R.equals(sortLongString(a), sortLongString(b))
                )
            );

            this.$subscribeTo(currentBreakdown$, () => {
                this.updateAvailableFields(this.selectedSources);
            });
        },
    },
    mounted() {
        window.vivid = this;
        this.observeBreakdown$();
    },
};
